import { createApi } from "@reduxjs/toolkit/query/react";
import axiosBaseQuery, { ApiErrorResponse } from "./axiosBaseQuery";
import FintreeConfig from "../app/config";
import User from "@/model/interface/User";
import ApplicantDocumentType from "@/model/interface/ApplicantDocumentType";
import MortgageShareApplication from "@/model/interface/MortgageShareApplication";
import RegisterData from "@/model/interface/RegisterData";
import RegisterResponse from "@/model/interface/RegisterResponse";
import ApplicationRequest from "@/model/interface/ApplicationRequest";
import FileResponse from "@/model/interface/FileResponse";
import FileData from "@/model/interface/FileData";
import { AxiosRequestHeaders } from "axios";
import ApiEmail from "@/model/interface/api/ApiEmail";
import { ApiApplication } from "@/model/interface/api/ApiApplication";
import ApiUser from "@/model/interface/api/ApiUser";
import ApplicantRequest from "@/model/interface/ApplicantRequest";
import ApplicantAddressRequest from "@/model/interface/ApplicantAddressRequest";
import ApplicantDocument from "@/model/interface/ApplicantDocument";
import { ApplicationStep } from "@/enum/ApplicationStep";
import { ApplicationStepStatus } from "@/enum/ApplicationStepStatus";
import ApplicantDocumentRequest from "@/model/interface/ApplicantDocumentRequest";
import ContactUsData from "@/model/interface/ContactUsData";
import EnquiryRequest from "@/model/interface/EnquiryRequest";

// Define a service using a base URL and expected endpoints
export const homeApi = createApi({
  reducerPath: "homeApi",
  baseQuery: axiosBaseQuery({
    baseUrl: `${FintreeConfig.baseAPIEndpoint}`,
  }),
  tagTypes: [
    "Home",
    "Application",
    "ApplicationStepStatus",
    "User",
    "File",
    "Email",
    "ApplicantDocumentType",
    "ApplicantDocument",
  ],
  endpoints: (builder) => ({
    getHome: builder.query<ApiUser, void>({
      query: () => ({
        url: `/v1/users/me`,
        method: "get",
      }),
      providesTags: ["Home"],
    }),
    getDocumentTypes: builder.query<ApplicantDocumentType[], void>({
      query: () => ({
        url: `/v1/documents/document-types`,
        method: "get",
      }),
      transformResponse: (response: any[]) =>
        response.map((dt: any) => ({
          ...dt,
          fields: typeof dt.fields === "string" ? JSON.parse(dt.fields) : dt.fields,
        })),
      providesTags: ["ApplicantDocumentType"],
    }),
    getApplicantDocuments: builder.query<ApplicantDocument[], string>({
      query: (applicantId: string) => ({
        url: `/v1/documents/applicants/${applicantId}`,
        method: "get",
      }),
      providesTags: (result, error, applicantId: string) => [{ type: "ApplicantDocument", applicantId }],
    }),
    getApplication: builder.query<ApiApplication, string>({
      query: (id: string) => ({ url: `/v1/applications/${id}`, method: "get" }),
      providesTags: (result, error, id: string) => [{ type: "Application", id }],
    }),
    getApplicationStepStatus: builder.query<Record<ApplicationStep, ApplicationStepStatus>, string>({
      query: (applicationId: string) => ({
        url: `/v1/applications/${applicationId}/step-status`,
        method: "get",
      }),
      providesTags: ["ApplicationStepStatus"],
    }),
    getApplications: builder.query<ApiApplication[], void>({
      query: () => ({ url: "/v1/applications", method: "get" }),
      providesTags: ["Application"],
    }),
    getMortgageShareApplication: builder.query<MortgageShareApplication, string>({
      query: (id: string) => ({ url: `/v2/mortgage_share_applications/${id}`, method: "get" }),
      providesTags: (result, error, id: string) => [{ type: "Application", id }],
    }),
    putHome: builder.mutation<ApiUser, User>({
      query: (userData) => ({
        url: `/v1/users/${userData.id}`,
        method: "put",
        data: userData,
      }),
      invalidatesTags: (result, error) => (error ? [] : ["Home"]),
    }),
    postContactUs: builder.mutation<void, ContactUsData>({
      query: (contactUsData) => ({
        url: `/v1/contact-us`,
        method: "post",
        data: contactUsData,
      }),
      invalidatesTags: (result, error) => (error ? [] : ["Home"]),
    }),
    postEnquiry: builder.mutation<void, EnquiryRequest>({
      query: (enquiry) => ({
        url: `/v1/enquiries`,
        method: "post",
        data: enquiry,
      }),
    }),
    postRegister: builder.mutation<RegisterResponse, RegisterData>({
      query: (registerData) => ({
        url: `/v1/register`,
        method: "post",
        data: registerData,
      }),
      invalidatesTags: (result, error) => (error ? [] : ["Home"]),
    }),
    postUpsertApplication: builder.mutation<ApiApplication, ApplicationRequest>({
      query: (formData) => ({
        url: `/v1/applications`,
        method: "post",
        data: formData,
      }),
      invalidatesTags: (result, error) => (error ? [] : ["Application"]),
    }),
    putApplication: builder.mutation<ApiApplication, ApplicationRequest>({
      query: (applicationData) => ({
        url: `/v2/applications/${applicationData.id}`,
        method: "put",
        data: applicationData,
      }),
      invalidatesTags: (result, error) => (error ? [] : ["Application"]),
    }),
    postApplicant: builder.mutation<ApiApplication, ApplicantRequest>({
      query: ({ applicationId, data }) => ({
        url: `/v1/applications/${applicationId}/applicants`,
        method: "post",
        data: data,
      }),
      invalidatesTags: (result, error) => (error ? [] : ["Application"]),
    }),
    postUpsertApplicantDocument: builder.mutation<ApplicantDocument, ApplicantDocumentRequest>({
      query: ({ applicantId, documentType, data }) => ({
        url: `/v1/documents/applicants/${applicantId}/${documentType}`,
        method: "post",
        data: data,
      }),
      invalidatesTags: (result, error) => (error ? [] : ["ApplicantDocument"]),
    }),
    putApplicant: builder.mutation<ApiApplication, ApplicantRequest>({
      query: ({ applicationId, data, applicantId }) => ({
        url: `/v1/applications/${applicationId}/applicants/${applicantId}`,
        method: "put",
        data: data,
      }),
      invalidatesTags: (result, error) => (error ? [] : ["Application"]),
    }),
    postApplicantAddress: builder.mutation<ApiApplication, ApplicantAddressRequest>({
      query: ({ applicationId, applicantId, data }) => ({
        url: `/v1/applications/${applicationId}/applicants/${applicantId}/addresses`,
        method: "post",
        data: data,
      }),
      invalidatesTags: (result, error) => (error ? [] : ["Application"]),
    }),
    getEmail: builder.query<ApiEmail, string>({
      query: (id: string) => ({ url: `/v1/emails/application-email/${id}`, method: "get" }),
      providesTags: (result, error, id: string) => [{ type: "Email", id }],
    }),
    postFile: builder.mutation<FileResponse, FileData>({
      query: ({ file, entity, entityId }) => {
        const formData = new FormData();
        formData.append("file[]", file);
        entity && formData.append("entity", entity);
        entityId && formData.append("entityId", entityId);

        return {
          url: `/v1/files`,
          method: "post",
          headers: {
            "Content-Type": "multipart/form-data;",
          } as AxiosRequestHeaders,
          data: formData,
        };
      },
      transformErrorResponse: (response: ApiErrorResponse, meta, arg) => {
        return response.detail ? `Upload failed - ${response.detail}` : "File Upload Failed";
      },
      invalidatesTags: (result, error) => (error ? [] : ["File"]),
    }),
  }),
});

export const {
  useGetHomeQuery,
  useLazyGetHomeQuery,
  useGetApplicationQuery,
  useGetEmailQuery,
  useGetApplicationsQuery,
  useGetMortgageShareApplicationQuery,
  useGetDocumentTypesQuery,
  useGetApplicantDocumentsQuery,
  useGetApplicationStepStatusQuery,
  usePutHomeMutation,
  usePostContactUsMutation,
  usePostEnquiryMutation,
  usePostRegisterMutation,
  usePostUpsertApplicationMutation,
  usePutApplicationMutation,
  usePostApplicantMutation,
  usePostUpsertApplicantDocumentMutation,
  usePutApplicantMutation,
  usePostApplicantAddressMutation,
  usePostFileMutation,
} = homeApi;
